// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { HistoricalBalancesChart } from '../historical-balances-chart';
import {
  DonutChartContainer,
  KpiBoxes,
  PeriodPicker,
  Portal,
} from '@shared/ui-kit/components';
import { Loader } from '../loader';
import { useSummary } from './use-summary';
import { AccountsSelector } from '../accounts-selector';
import { RefreshControl } from '../refresh-control';
import { TopPositions } from './top-positions';

export const Summary = defineComponent({
  displayName: 'Summary',
  useSetup: useSummary,
  View: ({
    t,
    kpis,
    status,
    historicalBalances,
    hasHistoricalBalances,
    currency,
    periodPicker,
    accountOptions,
    onChangeAccounts,
    selectedAccounts,
    updatedDate,
    onRefresh,
    institutionsBreakdown,
    institutionId,
    isSyncing,
    tags,
    tagsByObject,
    selectedTag,
    setSelectedTag,
    setActiveSerieId,
    holdings,
    refreshMode,
  }) => {
    return (
      <>
        <Portal container="aggregation-institutions-actions">
          <AccountsSelector
            data={accountOptions}
            onChange={onChangeAccounts}
            selectedAccounts={selectedAccounts}
            currency={currency}
            tags={tags}
            selectedTag={selectedTag}
            onTagSelect={setSelectedTag}
            tagsByObject={tagsByObject}
          />
        </Portal>

        <div className="flex flex-col gap-y-6" data-testid="summary-container">
          <div className="flex w-full flex-col items-stretch gap-y-3 md:flex-row md:items-center md:justify-between">
            <RefreshControl
              updatedDate={updatedDate}
              onRefresh={onRefresh}
              disabled={status === 'pending' || isSyncing}
              mode={refreshMode}
            />

            <div className="flex shrink-0 justify-end">
              <PeriodPicker
                onValueChange={periodPicker.onPeriodChange}
                defaultValue={periodPicker.defaultHist}
                availableOptions={['YTD', '1M', '3M', '6M', '1Y']}
              />
            </div>
          </div>

          <Loader
            status={status}
            shouldDisplayEmptyState={!hasHistoricalBalances}
            institution={institutionId}
            onRefresh={onRefresh}
          >
            <div className="mb-4 grid grid-cols-2 gap-6 md:grid-cols-3 xl:auto-cols-fr xl:grid-flow-col xl:grid-cols-none">
              {kpis.map(kpi => (
                <KpiBoxes key={kpi.dataTestid ?? kpi.text} {...kpi} />
              ))}
            </div>

            {hasHistoricalBalances && (
              <div className="flex flex-col gap-4 xl:grid xl:grid-cols-[minmax(0,2fr)_minmax(220px,1fr)] xl:items-stretch">
                <div className="flex h-full min-h-0 w-full min-w-0 flex-col">
                  <HistoricalBalancesChart
                    data={historicalBalances}
                    currency={currency}
                    onActiveSerieChange={setActiveSerieId}
                  />
                </div>

                <div className="flex h-full min-h-0 w-full min-w-0 flex-col">
                  <DonutChartContainer
                    name={t('glossary.bank-breakdown', { ns: 'glossary' })}
                    exposure={institutionsBreakdown.institutions}
                    weight={institutionsBreakdown.weights}
                    testId="summary-breakdown-donut-chart"
                    divider={false}
                    compactLayout
                  />
                </div>
              </div>
            )}

            {holdings.length > 0 && (
              <TopPositions
                positions={holdings}
                currency={currency}
                activePeriod={periodPicker.defaultHist}
              />
            )}
          </Loader>
        </div>
      </>
    );
  },
});
